/**
 * OnayMetniPencereleri.jsx — onay metinlerinin iki penceresi ve satırı (backlog #536 adım 6, #540, #553): Katılımcı portalındaki Fuar
 * Kuralları sayfası ve giriş GEÇİDİ (Routes/OnayKapisi.js) aynı parçaları çizer.
 *
 * İki pencere, her biri bir satırdan açılır:
 *  - KVKK Onamı: yalnızca `kvkk` metni, kullanıcının dilinde; metin sonuna kadar kaydırılınca "Onaylıyorum" açılır.
 *  - Fuar Kuralları: kalan kural metinleri art arda, tek bir metin gibi; solda bölümler listesi, en altta TEK bir "Onaylıyorum".
 * Metinler çağıranın elindedir (`metinler`): sayfa GET /current ile, geçit GET /pending ile okur; pencere hiçbir şey okumaz. Onay
 * kaydını da kendisi yazmaz: `onayla(metinler)` çağıranındır, hata fırlatırsa pencere gösterir.
 *
 * #553 (backend #552): bir metnin gövdesi HTML + sırayla dosyalardır (PDF sayfaları alt alta, görseller); hepsi AYNI kaydırma kutusunda
 * durur, böylece "sona kadar kaydırınca açılır" kuralı değişmez. Dosyalar inene kadar kutu son yüksekliğinde değildir: o süre
 * onay kapalı kalır ve dosyalar bitince "sondayım mı" yeniden ölçülür. FUAR NOTU (`ekNot`) kaydırma kutusunun ALTINDA okunur ve
 * pencerenin TEK onay düğmesiyle diğer metinlerle birlikte onaylanır (#668; WAS #553: ayrı kutucuk, işaretlenmeden düğme kapalı).
 * Not da bir metin gibi POST /{key}/accept ile kaydedilir.
 *
 * Taahhütnameler (hedef 3) burada YOK: onlar proje başına, proje sayfasında onaylanır (#216).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import { useTranslation } from "react-i18next";
import OnayMetniGovdesi, { DOSYA_DURUMU } from "./OnayMetniGovdesi";
import { DIL } from "../../services/onayMetniService";
import { BAGLAM } from "../../services/egitimBaglam";

export const KVKK_ANAHTARI = "kvkk";

/** Kaydırma kutusunun sonuna bu kadar piksel kala "sonuna gelindi" sayılır. */
const SON_PAYI = 8;

/** Kutu sonuna kadar kaydırıldı mı (ya da kaydırılacak kadar uzun değil mi). */
export const sonundaMi = (el) => Boolean(el) && el.scrollTop + el.clientHeight >= el.scrollHeight - SON_PAYI;

export const PENCERELER = {
  kvkk: { baslikAnahtari: "fuarKurallari.kvkkOnami", ikon: "bx bx-shield-quarter" },
  kurallar: { baslikAnahtari: "fuarKurallari.fuarKurallari", ikon: "bx bx-book-content" },
};

/**
 * Bir okumanın (GET /pending ya da /current) metinlerini iki pencereye böler (#540): `kvkk` kendi penceresine, GERİ KALAN HER ŞEY
 * (sonradan eklenmiş bir metin ve fuar notu dahil) kurallar penceresine, sortOrder sırasıyla; fuar notu (`ekNot`) hep SONDA. Boş
 * pencere yazılmaz. Bir pencere, içinde zorunlu bir metin varsa zorunludur.
 * @returns [{ ad: 'kvkk' | 'kurallar', metinler, anahtarlar, zorunlu }]
 */
export function pencereGruplari(metinler) {
  const sirali = [...(metinler || [])].sort((a, b) => (a.ekNot ? 1 : 0) - (b.ekNot ? 1 : 0) || (a.sortOrder || 0) - (b.sortOrder || 0));
  return [
    { ad: "kvkk", metinler: sirali.filter((m) => m.anahtar === KVKK_ANAHTARI) },
    { ad: "kurallar", metinler: sirali.filter((m) => m.anahtar !== KVKK_ANAHTARI) },
  ]
    .filter((g) => g.metinler.length > 0)
    .map((g) => ({ ...g, anahtarlar: g.metinler.map((m) => m.anahtar), zorunlu: g.metinler.some((m) => m.zorunlu !== false) }));
}

/** Bir metnin gövdesi; dosya durumunu pencerenin haritasına anahtarıyla bildirir, kaldırılınca siler. */
function MetinGovdesi({ m, bildir }) {
  const durumBildir = useCallback((d) => bildir(m.anahtar, d), [bildir, m.anahtar]);
  useEffect(() => () => bildir(m.anahtar, null), [bildir, m.anahtar]);
  return <OnayMetniGovdesi icerik={m.icerik} dosyalar={m.dosyalar} onDurum={durumBildir} />;
}

/**
 * Bir onay penceresi. `coklu` iken metinler art arda bölüm bölüm çizilir ve solda (telefonda üstte) bölümler listesi durur.
 * Onay düğmesi, metin kutusu sonuna kadar kaydırılınca (ve dosyalar inince, zorunlu fuar notu işaretlenince) açılır.
 *
 * @param metinler  pencerenin metinleri (okuyucu satırları: baslik, icerik, dosyalar, ekNot, ...); fuar notları kutunun altında çizilir
 * @param onayla    async (metinler) => void: seçilen metinlerin onayını kaydeder; hata fırlatırsa pencere mesajı gösterir ve açık kalır
 * @param salt      true iken onay düğmesi kapalıdır (personelin firma önizlemesi: oradan hiçbir şey kaydedilmez)
 */
export function OnayPenceresi({ acik, kapat, baslik, coklu, dilId, metinler, onaylandi, onayla, salt }) {
  const { t } = useTranslation("portal");
  const [sonunda, setSonunda] = useState(false);
  const [dosyaDurum, setDosyaDurum] = useState({});
  const [aktifBolum, setAktifBolum] = useState(null);
  const [onaylaniyor, setOnaylaniyor] = useState(false);
  const [hata, setHata] = useState(null);
  const kutu = useRef(null);
  const bolumler = useRef({});

  const govdeler = useMemo(() => metinler.filter((m) => !m.ekNot), [metinler]);
  const notlar = metinler.filter((m) => m.ekNot);
  const anahtarlar = useMemo(() => govdeler.map((m) => m.anahtar), [govdeler]);

  const dosyaBildir = useCallback((anahtar, d) => setDosyaDurum((o) => {
    if (d == null) {
      if (!(anahtar in o)) return o;
      const kalan = { ...o };
      delete kalan[anahtar];
      return kalan;
    }
    return o[anahtar] === d ? o : { ...o, [anahtar]: d };
  }), []);

  // Dosyası olan bir metin, dosyaları inene (ya da hata verene) kadar "bekliyor"; dosyasız metin hiç beklemez.
  const dosyaBekliyor = metinler.some((m) => (m.dosyalar || []).length > 0
    && dosyaDurum[m.anahtar] !== DOSYA_DURUMU.HAZIR && dosyaDurum[m.anahtar] !== DOSYA_DURUMU.HATA);
  const dosyaHatasi = metinler.some((m) => dosyaDurum[m.anahtar] === DOSYA_DURUMU.HATA);

  // Kutu yalnızca pencere açılınca var olur: takıldığı an bir kez ölçülür, kaydırılacak kadar uzun değilse düğme hemen açılır.
  const kutuyuTak = useCallback((el) => {
    kutu.current = el;
    if (el) setTimeout(() => setSonunda(sonundaMi(el)), 0);
  }, []);

  // Dosyalar inince kutu uzar (ilk ölçüm kısa kutuda yapılmış olabilir): son yükseklikte yeniden ölç.
  useEffect(() => {
    if (!acik || dosyaBekliyor) return undefined;
    const id = setTimeout(() => setSonunda(sonundaMi(kutu.current)), 0);
    return () => clearTimeout(id);
  }, [acik, dosyaBekliyor]);

  const kaydirildi = useCallback(() => {
    const el = kutu.current;
    if (!el) return;
    if (sonundaMi(el)) setSonunda(true);
    if (!coklu) return;
    // Görünen bölüm: üst kenarı kutunun üstünü geçmiş son bölüm.
    let gorunen = anahtarlar[0];
    for (const a of anahtarlar) {
      const b = bolumler.current[a];
      if (b && b.offsetTop <= el.scrollTop + 24) gorunen = a;
    }
    setAktifBolum(gorunen);
  }, [coklu, anahtarlar]);

  const bolumeGit = (anahtar) => {
    const el = kutu.current;
    const b = bolumler.current[anahtar];
    if (!el || !b) return;
    if (typeof el.scrollTo === "function") el.scrollTo({ top: b.offsetTop, behavior: "smooth" });
    else el.scrollTop = b.offsetTop;
    setAktifBolum(anahtar);
  };

  // Okunamayan bir metin (ya da dosyası) onaylanamaz: kullanıcı görmediği metni onaylamış olmasın.
  const okunamayan = metinler.some((m) => m.hata) || dosyaHatasi;

  const onayTiklandi = async () => {
    setOnaylaniyor(true);
    setHata(null);
    try {
      // #668: fuar notu da dahil, pencerenin her metni tek düğmeyle onaylanır. WAS: not yalnızca kutucuğu işaretliyse gönderilirdi.
      await onayla(metinler);
    } catch (e) {
      setHata(e.message);
    } finally {
      setOnaylaniyor(false);
    }
  };

  const kapatici = () => { setHata(null); kapat(); };
  const kvkkBasligi = !coklu && govdeler[0] && !govdeler[0].hata ? govdeler[0].baslik : baslik;
  const aktif = aktifBolum && anahtarlar.includes(aktifBolum) ? aktifBolum : anahtarlar[0];

  let ipucu = "";
  if (!onaylandi) {
    if (!sonunda || dosyaBekliyor) ipucu = t("fuarKurallari.sonunaKadarKaydirin");
  }

  return (
    <Modal isOpen={acik} toggle={onaylaniyor ? undefined : kapatici} size={coklu ? "xl" : "lg"} centered className="fuar-kurallari-modal">
      <ModalHeader toggle={onaylaniyor ? undefined : kapatici}>{kvkkBasligi}</ModalHeader>
      {/* data-tour-baglam: kullanım turu (services/egitimTuru.js) açık pencereyi bununla tanır; KVKK ve Fuar Kuralları ayrı anlatılır. */}
      <ModalBody className="p-0" data-tour-baglam={coklu ? BAGLAM.ONAY_KURALLAR : BAGLAM.ONAY_KVKK}>
        <div className="d-flex flex-column flex-md-row">
          {coklu && (
            <nav className="fuar-kurallari-menu" aria-label={t("fuarKurallari.bolumler")} data-tour="onay-pencere-bolumler">
              {govdeler.map((m) => (
                <button key={m.anahtar} type="button" onClick={() => bolumeGit(m.anahtar)}
                  className={`fuar-kurallari-menu__oge${aktif === m.anahtar ? " fuar-kurallari-menu__oge--aktif" : ""}`}
                  data-testid={`menu-${m.anahtar}`}>
                  {m.baslik}
                </button>
              ))}
            </nav>
          )}
          <div ref={kutuyuTak} onScroll={kaydirildi} className="fuar-kurallari-kaydirma flex-grow-1" data-testid="kaydirma"
            data-tour="onay-pencere-metin">
            {govdeler.map((m, i) => (
              <section key={m.anahtar} ref={(el) => { bolumler.current[m.anahtar] = el; }} data-testid={`bolum-${m.anahtar}`}>
                {coklu && <h5 className="fuar-kurallari-bolum-basligi">{m.baslik}</h5>}
                {!m.hata && dilId === DIL.EN && m.languageId === DIL.TR && (
                  <p className="small text-muted fst-italic">{t("fuarKurallari.ingilizcesiYok")}</p>
                )}
                {m.hata
                  ? <Alert color="warning" className="small">{m.hata}</Alert>
                  : <MetinGovdesi m={m} bildir={dosyaBildir} />}
                {i < govdeler.length - 1 && <hr className="my-4" />}
              </section>
            ))}
          </div>
        </div>
        {notlar.length > 0 && (
          <div className="fuar-kurallari-not" data-testid="ek-not" data-tour="onay-pencere-not">
            {notlar.map((n) => (
              <div key={n.anahtar} className="mb-2" data-testid={`not-${n.anahtar}`}>
                <div className="fw-semibold small mb-1">{n.baslik}</div>
                <div className="small fuar-kurallari-not__govde"><MetinGovdesi m={n} bildir={dosyaBildir} /></div>
              </div>
            ))}
          </div>
        )}
      </ModalBody>
      <ModalFooter className="justify-content-between">
        {hata
          ? <span className="small text-danger" data-testid="onay-hatasi">{hata}</span>
          : <span className="small text-muted">{ipucu}</span>}
        <div className="d-flex gap-2">
          <Button color="light" onClick={kapatici} disabled={onaylaniyor}>{t("fuarKurallari.kapat")}</Button>
          <Button color="primary" onClick={onayTiklandi} data-testid="onayla" data-tour="onay-pencere-onayla"
            disabled={onaylandi || !sonunda || dosyaBekliyor || onaylaniyor || salt || okunamayan || metinler.length === 0}
            title={salt ? t("fuarKurallari.onizlemeNotu") : undefined}>
            {onaylandi
              ? <><i className="bx bx-check me-1" />{t("fuarKurallari.onaylandi")}</>
              : <>{onaylaniyor && <Spinner size="sm" className="me-1" />}{t("fuarKurallari.okudumOnayliyorum")}</>}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
}

/** Listedeki bir satır: solda simge, ortada ad ve durum, sağda onay kutusu. Satırın tamamı pencereyi açar. */
export function KuralSatiri({ ikon, ad, onaylandi, ac, testId, isteginBagli, tour }) {
  const { t } = useTranslation("portal");
  return (
    <button type="button" onClick={ac} className="fuar-kurali-satir" data-testid={testId} data-tour={tour}
      data-onaylandi={onaylandi ? "1" : undefined}>
      <span className="fuar-kurali-satir__ikon"><i className={ikon} /></span>
      <span className="flex-grow-1 text-start">
        <span className="d-block fw-semibold fs-15">
          {ad}
          {isteginBagli && <span className="small fw-normal text-muted ms-2" data-testid={`${testId}-istege-bagli`}>{t("onayKapisi.istegeBagli")}</span>}
        </span>
        {onaylandi
          ? <span className="d-block small fuar-kurali-onaylandi"><i className="bx bx-check-circle me-1" />{t("fuarKurallari.onaylandi")}</span>
          : <span className="d-block small text-muted">{t("fuarKurallari.okuyupOnaylayin")}</span>}
      </span>
      <i className={`bx ${onaylandi ? "bx-checkbox-checked fuar-kurali-onaylandi" : "bx-checkbox text-muted"} fuar-kurali-satir__kutu`}
        aria-label={onaylandi ? t("fuarKurallari.onaylandi") : t("fuarKurallari.onaylanmadi")} role="img" />
    </button>
  );
}
